import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Tooltip, Select } from "antd";
import { IoCloseOutline, IoLocation, IoCheckmark } from "react-icons/io5";
import { FiPlus, FiUpload } from "react-icons/fi";
import { LuEye, LuEyeOff, LuImage, LuLock } from "react-icons/lu";
import { toast } from "react-toastify";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import methodModel from "../../methods/methods";
import Layout from "../../components/global/layout";
import FormControl from "../../components/common/FormControl";
import shared from "./shared";
import { CgArrowLeft, CgFormatCenter } from "react-icons/cg";
import GooglePlaceAutoComplete from "../../components/common/GooglePlaceAutoComplete/GooglePlace";
import environment from "../../environment";
import { FaAngleLeft, FaChevronLeft } from "react-icons/fa";
import { RiFileList3Line } from "react-icons/ri";
import { IoIosHourglass, IoMdImages } from "react-icons/io";
import { MdOutlineAddLocationAlt, MdOutlinePlace } from "react-icons/md";
import { TbBuildingStore } from "react-icons/tb";
import { validateLocaleAndSetLanguage } from "typescript";

const { Option } = Select;

// ─── Password strength rules ───────────────────────────────────────────────
const PASSWORD_RULES = [
  {
    key: "minLength",
    label: "At least 8 characters",
    test: (v) => v.length >= 8,
  },
  {
    key: "uppercase",
    label: "At least 1 uppercase letter",
    test: (v) => /[A-Z]/.test(v),
  },
  {
    key: "lowercase",
    label: "At least 1 lowercase letter",
    test: (v) => /[a-z]/.test(v),
  },
  { key: "number", label: "At least 1 number", test: (v) => /[0-9]/.test(v) },
  {
    key: "special",
    label: "At least 1 special character",
    test: (v) => /[^A-Za-z0-9]/.test(v),
  },
];
const validatePassword = (v) =>
  PASSWORD_RULES.map((r) => ({ ...r, passed: r.test(v) }));
const isPasswordValid = (v) => PASSWORD_RULES.every((r) => r.test(v));

const DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

// null = closed, '' or string = open with that value
const defaultHours = DAYS.reduce((acc, d) => ({ ...acc, [d]: "" }), {});

const AMENITY_OPTIONS = [
  "Live Music",
  "Rooftop Seating",
  "Free WiFi",
  "Parking",
  "Bar",
  "Restaurant",
  "Pool",
  "Valet",
  "Outdoor Seating",
  "Private Dining",
  "Karaoke",
  "Others",
];

const VENUE_TYPES = [
  "lounge",
  "bar",
  "restaurant",
  "club",
  "cafe",
  "hotel",
  "rooftop",
  "pub",
  "other",
];

// ─── Default single location ───────────────────────────────────────────────
const defaultLocation = () => ({
  address: "",
  city: "",
  state: "",
  country: "",
  pinCode: "",
  latitude: "",
  longitude: "",
  hours: { ...defaultHours },
  sameAsPrevious: false,
  stations: [{ name: "" }],
});

function AddEditVenue() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    logo: "",
    website: "",
    description: "",
    images: [],
    type: "",
    currencySymbol: "",
    allowGlobalPoints: false,
    isRewardAllowed: true,
    currency: "",
    amenities: [],
    locations: [defaultLocation()],
    mobileNo: '',
    dialCode: '',
    landlineNo: '',
  });

  const [DeletedIds, setDeletedIds] = useState({ stations: [], locations: [] });
  const [submitted, setSubmitted] = useState(false);
  const [eyes, setEyes] = useState({ password: false, confirmPassword: false });

  const [customAmenity, setCustomAmenity] = useState("");
  const [showCustomAmenity, setShowCustomAmenity] = useState(false);

  // ── Password derived values ──
  const passwordFilled = form.password.length > 0;
  const passwordRules = validatePassword(form.password);
  const passwordValid = isPasswordValid(form.password);
  const confirmMatch = form.confirmPassword === form.password;
  const passedCount = passwordRules.filter((r) => r.passed).length;
  const strengthLabel =
    passedCount <= 1
      ? "Weak"
      : passedCount <= 3
        ? "Fair"
        : passedCount === 4
          ? "Good"
          : "Strong";
  const strengthColor =
    passedCount <= 1
      ? "#ef4444"
      : passedCount <= 3
        ? "#f59e0b"
        : passedCount === 4
          ? "#3b82f6"
          : "#22c55e";

  const inValidEmail = methodModel.emailvalidation(form.email);

  useEffect(() => {
    if (id) {
      loader(true);
      ApiClient.get(shared.detailApi, { id }).then((res) => {
        if (res.success) {
          const d = res.data;
          // ── Normalise locations array ──
          let loadedLocations = [defaultLocation()];
          if (d.locations && d.locations.length > 0) {
            loadedLocations = d.locations.map((loc, idx) => {
              const loadedHours = { ...defaultHours };
              if (loc.hours) {
                DAYS.forEach((day) => {
                  loadedHours[day] =
                    loc.hours[day] === null ? null : loc.hours[day] || "";
                });
              }
              return {
                id: loc?._id || loc?.id || null,
                address: loc.address || "",
                city: loc.city || "",
                state: loc.state || "",
                country: loc.country || "",
                pinCode: loc.pinCode || "",
                latitude:
                  loc.currentLocation?.coordinates?.[1]?.toString() || "",
                longitude:
                  loc.currentLocation?.coordinates?.[0]?.toString() || "",
                hours: loadedHours,
                sameAsPrevious: false,
                stations:
                  loc.stations?.length > 0
                    ? loc.stations?.map((_itm) => ({
                        id: _itm?._id || _itm?.id,
                        name: _itm?.name,
                      }))
                    : [{ name: "" }],
              };
            });
          } else if (d.address || d.city) {
            // ── Backward-compat: old single-location fields ──
            const loadedHours = { ...defaultHours };
            if (d.hours) {
              DAYS.forEach((day) => {
                loadedHours[day] =
                  d.hours[day] === null ? null : d.hours[day] || "";
              });
            }
            loadedLocations = [
              {
                address: d.address || "",
                city: d.city || "",
                state: d.state || "",
                country: d.country || "",
                pinCode: d.pinCode || "",
                latitude: d.latitude || "",
                longitude: d.longitude || "",
                hours: loadedHours,
                sameAsPrevious: false,
                stations:
                  d.stations?.length > 0
                    ? d.stations?.map((_itm) => ({
                        id: _itm?._id || _itm?.id,
                        name: _itm?.name,
                      }))
                    : [{ name: "" }],
              },
            ];
          }

          const dial = d.dialCode || '';
          const mob = d.mobileNo ? (d.mobileNo.startsWith(dial) ? d.mobileNo : `${dial}${d.mobileNo}`) : '';

          setForm({
            name: d.name || "",
            email: d.email || "",
            password: "",
            confirmPassword: "",
            logo: d.logo || "",
            website: d.website || "",
            description: d.description || "",
            images: d.images || [],
            type: d.type || "",
            currencySymbol: d.currencySymbol || "",
            currency: d.currency || "",
            amenities: d.amenities || [],
            locations: loadedLocations,
            allowGlobalPoints: d?.allowGlobalPoints ?? false,
            isRewardAllowed: d?.isRewardAllowed ?? true,
            id: d.id || d._id,
            mobileNo: mob || "",
            dialCode: dial || "",
            landlineNo: d.landlineNo || '',
          });
        }
        loader(false);
      });
    }
  }, [id]);

  // ──────────────────────────────────────────────────────────────────────────
  //  LOCATION helpers
  // ──────────────────────────────────────────────────────────────────────────

  const handleAmenityChange = (values) => {
    if (values.includes("Others")) {
      setShowCustomAmenity(true);
      return;
    }

    setForm((prev) => ({ ...prev, amenities: values }));
  };

  const submitCustomAmenity = () => {
    const value = customAmenity.trim();

    if (!value) {
      toast.error("Please enter an amenity");
      return;
    }

    const alreadyExists = form.amenities.some(
      (amenity) => amenity.toLowerCase() === value.toLowerCase(),
    );

    if (alreadyExists) {
      toast.error("This amenity already exists");
      return;
    }

    setForm((prev) => ({
      ...prev,
      amenities: [...prev.amenities, value],
    }));

    setCustomAmenity("");
    setShowCustomAmenity(false);
  };

  const cancelCustomAmenity = () => {
    setCustomAmenity("");
    setShowCustomAmenity(false);
  };

  const updateLocation = (index, key, value) => {
    setForm((f) => {
      const locations = [...f.locations];
      locations[index] = { ...locations[index], [key]: value };
      return { ...f, locations };
    });
  };

  const addLocation = () => {
    setForm((f) => ({ ...f, locations: [...f.locations, defaultLocation()] }));
  };

  const removeLocation = (index) => {
    setForm((f) => {
      const locations = f.locations.filter((_, i) => i !== index);
      return { ...f, locations };
    });
  };

  // ── Hours helpers per location ──
  const isClosedDay = (locIndex, day) =>
    form.locations[locIndex].hours[day] === null;

  const toggleDay = (locIndex, day) => {
    setForm((f) => {
      const locations = [...f.locations];
      const hours = { ...locations[locIndex].hours };
      hours[day] = hours[day] === null ? "" : null;
      locations[locIndex] = { ...locations[locIndex], hours };
      return { ...f, locations };
    });
  };

  const setHour = (locIndex, day, value) => {
    setForm((f) => {
      const locations = [...f.locations];
      const hours = { ...locations[locIndex].hours };
      hours[day] = value;
      locations[locIndex] = { ...locations[locIndex], hours };
      return { ...f, locations };
    });
  };

  // ── "Same as previous" toggle ──
  const toggleSameAsPrevious = (locIndex) => {
    setForm((f) => {
      const locations = [...f.locations];
      const current = locations[locIndex];
      const newSame = !current.sameAsPrevious;
      if (newSame && locIndex > 0) {
        // Copy hours from the previous location
        locations[locIndex] = {
          ...current,
          sameAsPrevious: true,
          hours: { ...locations[locIndex - 1].hours },
        };
      } else {
        locations[locIndex] = { ...current, sameAsPrevious: newSame };
      }
      return { ...f, locations };
    });
  };

  // ──────────────────────────────────────────────────────────────────────────
  //  STATION helpers
  // ──────────────────────────────────────────────────────────────────────────
  const addStation = (locIndex) => {
    setForm((f) => {
      const locations = [...f.locations];
      locations[locIndex] = {
        ...locations[locIndex],
        stations: [...locations[locIndex].stations, { name: "" }],
      };
      return { ...f, locations };
    });
  };

  const removeStation = (locIndex, stationIndex) => {
    setForm((f) => {
      const locations = [...f.locations];
      const stations = locations[locIndex].stations.filter(
        (_, i) => i !== stationIndex,
      );
      locations[locIndex] = { ...locations[locIndex], stations };
      return { ...f, locations };
    });
  };

  const updateStation = (locIndex, stationIndex, value) => {
    setForm((f) => {
      const locations = [...f.locations];
      const stations = [...locations[locIndex].stations];
      stations[stationIndex] = { ...stations[stationIndex], name: value };
      locations[locIndex] = { ...locations[locIndex], stations };
      return { ...f, locations };
    });
  };

  // ──────────────────────────────────────────────────────────────────────────
  //  SUBMIT
  // ──────────────────────────────────────────────────────────────────────────
  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);

    if (!form.name) return;
    if (!form.email || !inValidEmail) return;

    if (!id) {
      if (!passwordValid) return;
      if (!confirmMatch) return;
    } else if (passwordFilled) {
      if (!passwordValid) return;
      if (!confirmMatch) return;
    }

    if (!form.type) return;

    // Validate at least one location has city + country
    const firstLoc = form.locations[0];
    if (!firstLoc.city) return;
    if (!firstLoc.country) return;

    // Build locations payload

    DeletedIds.stations.map((_stationId) => {
      ApiClient.delete(`venue/station/delete`, { id: _stationId }, true);
    });
    DeletedIds.locations.map((_locationId) => {
      ApiClient.delete(`venue/location/delete`, { id: _locationId }, true);
    });
    const locationsPayload = form.locations.map((loc) => {
      const hoursPayload = {};
      DAYS.forEach((day) => {
        hoursPayload[day] =
          loc.hours[day] === null ? null : loc.hours[day] || "";
      });

      const stationsPayload = loc.stations
        .filter((s) => s.name.trim() !== "")
        .map((s) => ({ id: s?._id || s?.id, name: s.name.trim() }));
      const finalLocationPayload = {
        address: loc.address,
        city: loc.city,
        state: loc.state,
        country: loc.country,
        pinCode: loc.pinCode,
        currentLocation: {
          type: "Point",
          coordinates: [
            parseFloat(loc.longitude) || 0,
            parseFloat(loc.latitude) || 0,
          ],
        },
        hours: hoursPayload,
        stations: stationsPayload,
      };
      if (loc?.id) {
        finalLocationPayload["id"] = loc?.id;
      }
      return finalLocationPayload;
    });

    let mobileNo = form.mobileNo || '';
    if (form.dialCode && mobileNo.startsWith(form.dialCode)) {
      mobileNo = mobileNo.substring(form.dialCode.length);
    }

    const payload = {
      name: form.name,
      email: form.email,
      logo: form.logo,
      website: form.website,
      allowGlobalPoints: form?.allowGlobalPoints,
      isRewardAllowed: form?.isRewardAllowed,
      description: form.description,
      images: form.images,
      type: form.type,
      currencySymbol: form.currencySymbol,
      currency: form.currency,
      amenities: form.amenities,
      locations: locationsPayload,
      mobileNo: mobileNo,
      dialCode: form.dialCode,
      landlineNo: form.landlineNo,
    };

    if (passwordFilled) payload.password = form.password;
    if (id) {
      payload.id = id;
    } else {
      payload["approvalStatus"] = "approved";
    }

    loader(true);
    const method = id ? "put" : "post";
    const url = id ? shared.editApi : shared.addApi;
    // console.log("here is payload :",payload )
    ApiClient.allApi(url, payload, method).then((res) => {
      if (res.success) {
        navigate(`/${shared.url}`);
      }
      loader(false);
    });
  };

  // ── Logo upload ──
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.error("Only JPG/PNG images are allowed");
      return;
    }
    loader(true);
    ApiClient.postFormData("upload/image?model=venue", { file }).then((res) => {
      if (res.success)
        setForm((f) => ({ ...f, logo: res?.data?.filePath || res?.filePath }));
      loader(false);
    });
  };

  // ── Multi-image upload ──
  const handleImagesUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const invalidFile = files.find(
      (file) => !["image/jpeg", "image/png", "image/jpg"].includes(file.type),
    );
    if (invalidFile) {
      toast.error("Only JPG/PNG images are allowed");
      return;
    }

    loader(true);
    const promises = files.map((file) =>
      ApiClient.postFormData("upload/image?model=venue", { file }),
    );

    Promise.all(promises)
      .then((results) => {
        const paths = results
          .filter((res) => res.success)
          .map((res) => res?.data?.filePath || res?.filePath);

        if (paths.length > 0) {
          setForm((f) => ({ ...f, images: [...f.images, ...paths] }));
        }
        loader(false);
      })
      .catch((err) => {
        console.error("Upload error", err);
        loader(false);
      });
  };

  const removeImage = (index) =>
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== index) }));

  // ── UI helpers ──
  const SectionHead = ({ title }) => (
    <h4 className="text-[#2D2D2D] font-semibold">{title}</h4>
  );
  const RequiredStar = () => <span className="text-red-500 ml-0.5">*</span>;
  const ErrorMsg = ({ show, msg }) =>
    show ? <p className="text-red-500 text-xs mt-1">{msg}</p> : null;

  return (
    <Layout>
      <form onSubmit={handleSubmit}>
        <div className="pprofile1">
          {/* ── Header ── */}
          <div className="flex gap-3 mb-8">
            <Tooltip placement="top" title="Back">
              <Link
                to={`/${shared.url}`}
                className="min-w-[30px] w-[30px] h-[30px] rounded-full bg-[#CC9900] text-white flex items-center justify-center hover:opacity-80"
              >
                <FaChevronLeft />
              </Link>
            </Tooltip>
            <div className="">
              <h3 className="text-lg lg:text-2xl font-semibold text-[#221E10]">
                {id ? "Edit" : "Add"} {shared.addTitle}
              </h3>
              <p className="text-sm text-[#2D2D2D]">
                Fill in the details below to create a new venue
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6">
            {/* ── Basic Info ── */}
            <div className="bg-white p-5 rounded-[12px] shadow-[0px_3px_14px_-8px_#888888] border">
              <div className="mb-5">
                <div className="flex gap-2">
                  <RiFileList3Line className="text-[#CC9900] text-xl relative top-[1px]" />
                  <SectionHead title="Basic Information" />
                </div>
                <p className="text-sm text-[#717182]">
                  Enter the essential details about your venue
                </p>
              </div>

              <div className="grid grid-cols-12 gap-5">
                <div className="col-span-12 sm:col-span-6">
                  <FormControl
                    type="text"
                    name="name"
                    label="Venue Name"
                    placeholder="e.g. Skyline Lounge"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e })}
                    required
                  />
                  <ErrorMsg
                    show={submitted && !form.name}
                    msg="Venue name is required"
                  />
                </div>

                <div className="col-span-12 sm:col-span-6">
                  <FormControl
                    type="text"
                    name="email"
                    label="Email"
                    placeholder="venue@example.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e })}
                    required
                    disabled={!!id}
                    className={id ? "cursor-not-allowed" : ""}
                  />
                  <ErrorMsg
                    show={submitted && !form.email}
                    msg="Email is required"
                  />
                  <ErrorMsg
                    show={!!(submitted && form.email && !inValidEmail)}
                    msg="Please enter a valid email"
                  />
                </div>

                <div className="col-span-12 sm:col-span-6">
                  <FormControl
                    type="url"
                    name="website"
                    label="Website"
                    placeholder="https://example.com"
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e })}
                  />
                </div>

                <div className="col-span-12 sm:col-span-6">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Venue Type <RequiredStar />
                  </label>
                  <Select
                    className="w-full"
                    placeholder="Select type"
                    value={form.type || undefined}
                    onChange={(v) => setForm({ ...form, type: v })}
                  >
                    {VENUE_TYPES.map((t) => (
                      <Option key={t} value={t}>
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </Option>
                    ))}
                  </Select>
                  <ErrorMsg
                    show={submitted && !form.type}
                    msg="Venue type is required"
                  />
                </div>
                {/* ── Mobile Number & Landline Number ── */}
                <div className="col-span-12 sm:col-span-6 mobile_number">
                  <FormControl
                    type="phone"
                    name="mobileNo"
                    label="Mobile Number"
                    value={form.mobileNo}
                    onChange={(val, dialCode) => setForm({ ...form, mobileNo: val, dialCode: dialCode || form.dialCode })}
                  />
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <FormControl
                    type="tel"
                    name="telephone"
                    label="Telephone (Landline)"
                    placeholder="e.g. 0112345678"
                    maxlength="15"
                    value={form.landlineNo}
                    autoComplete="off"
                    onChange={(val) => setForm({ ...form, landlineNo: val.replace(/[^0-9]/g, '') })}
                  />
                </div>

                {/* allowGlobalPoints */}
                <div className="col-span-12 sm:col-span-6 flex flex-col justify-between p-4 bg-gray-50 border border-gray-100 rounded-xl hover:border-[#CC9900] transition-colors duration-200">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1">
                      Allow Customer to use your points globally?
                    </label>
                    <p className="text-xs text-gray-500 mb-4">
                      Enable this to allow points collected at this venue to be
                      redeemed at any global location.
                    </p>
                  </div>
                  <div className="flex bg-gray-200/60 p-1 rounded-lg w-full max-w-[200px]">
                    <button
                      type="button"
                      onClick={() =>
                        setForm({ ...form, allowGlobalPoints: true })
                      }
                      className={`flex-1 text-center py-1.5 text-xs font-semibold rounded-md transition-all duration-200 ${form.allowGlobalPoints ? "bg-[#CC9900] text-white shadow" : "text-gray-600 hover:text-gray-800"}`}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setForm({ ...form, allowGlobalPoints: false })
                      }
                      className={`flex-1 text-center py-1.5 text-xs font-semibold rounded-md transition-all duration-200 ${!form.allowGlobalPoints ? "bg-gray-700 text-white shadow" : "text-gray-600 hover:text-gray-800"}`}
                    >
                      No
                    </button>
                  </div>
                </div>

                {/* isRewardAllowed */}
                <div className="col-span-12 sm:col-span-6 flex flex-col justify-between p-4 bg-gray-50 border border-gray-100 rounded-xl hover:border-[#CC9900] transition-colors duration-200">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1">
                      Reward System implemented?
                    </label>
                    <p className="text-xs text-gray-500 mb-4">
                      Determine if users can earn and redeem rewards at this
                      venue.
                    </p>
                  </div>
                  <div className="flex bg-gray-200/60 p-1 rounded-lg w-full max-w-[200px]">
                    <button
                      type="button"
                      onClick={() =>
                        setForm({ ...form, isRewardAllowed: true })
                      }
                      className={`flex-1 text-center py-1.5 text-xs font-semibold rounded-md transition-all duration-200 ${form.isRewardAllowed ? "bg-[#CC9900] text-white shadow" : "text-gray-600 hover:text-gray-800"}`}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setForm({ ...form, isRewardAllowed: false })
                      }
                      className={`flex-1 text-center py-1.5 text-xs font-semibold rounded-md transition-all duration-200 ${!form.isRewardAllowed ? "bg-gray-700 text-white shadow" : "text-gray-600 hover:text-gray-800"}`}
                    >
                      No
                    </button>
                  </div>
                </div>

                <div className="col-span-12">
                  <FormControl
                    type="textarea"
                    name="description"
                    label="Description"
                    placeholder="Describe the venue..."
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e })}
                  />
                </div>
              </div>
            </div>

            {/* ── Password ── */}
            <div className="bg-white p-5 rounded-[12px] shadow-[0px_3px_14px_-8px_#888888] border">
              <div className="mb-5">
                <div className="flex gap-2">
                  <LuLock className="text-[#CC9900] text-xl relative top-[1px]" />
                  <SectionHead title={id ? "Change Password" : "Password"} />
                </div>
                <p className="text-sm text-[#717182]">
                  Set a strong password for your venue account
                </p>
              </div>
              <div className="grid grid-cols-12 gap-5">
                <div className="col-span-12 sm:col-span-6">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Password{!id && <RequiredStar />}
                    {id && (
                      <span className="text-gray-400 text-xs font-normal ml-1">
                        (leave blank to keep unchanged)
                      </span>
                    )}
                  </label>
                  <div className="relative">
                    <input
                      type={eyes.password ? "text" : "password"}
                      className="relative  bg-[#F7F7F7] w-full rounded-lg h-10 focus:outline focus:outline-[1px] focus:outline-[#e5e7eb] focus:ring-0 px-3 disabled:cursor-not-allowed pr-10"
                      value={form.password}
                      maxLength={16}
                      placeholder={
                        id ? "Enter new password (optional)" : "Enter password"
                      }
                      onChange={(e) =>
                        setForm({
                          ...form,
                          password: e.target.value,
                          confirmPassword:
                            e.target.value === "" ? "" : form.confirmPassword,
                        })
                      }
                      required={!id}
                    />
                    <div className="absolute top-1/2 -translate-y-1/2 right-3 cursor-pointer">
                      <i
                        onClick={() =>
                          setEyes({ ...eyes, password: !eyes.password })
                        }
                      >
                        {eyes.password ? (
                          <LuEye className="text-gray-400" />
                        ) : (
                          <LuEyeOff className="text-gray-400" />
                        )}
                      </i>
                    </div>
                  </div>

                  <div style={{ paddingTop: "8px", overflow: "hidden" }}>
                    <div style={{ opacity: 1, transition: "opacity 0.2s" }}>
                      <div
                        style={{
                          display: "flex",
                          gap: "4px",
                          marginBottom: "4px",
                        }}
                      >
                        {[1, 2, 3, 4, 5].map((n) => (
                          <div
                            key={n}
                            style={{
                              flex: 1,
                              height: "4px",
                              borderRadius: "9999px",
                              background:
                                n <= passedCount ? strengthColor : "#e5e7eb",
                              transition: "background 0.25s",
                            }}
                          />
                        ))}
                      </div>
                      <p
                        style={{
                          fontSize: "11px",
                          fontWeight: 600,
                          color: strengthColor,
                          margin: "0 0 6px 0",
                          transition: "color 0.2s",
                        }}
                      >
                        {strengthLabel}
                      </p>
                    </div>
                    <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                      {passwordRules.map((rule) => (
                        <li
                          key={rule.key}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            fontSize: "11px",
                            lineHeight: "1.6",
                            color: !passwordFilled
                              ? "#9ca3af"
                              : rule.passed
                                ? "#22c55e"
                                : "#ef4444",
                            transition: "color 0.2s",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "10px",
                              width: "10px",
                              flexShrink: 0,
                            }}
                          >
                            {!passwordFilled ? "·" : rule.passed ? "✓" : "✗"}
                          </span>
                          {rule.label}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {submitted && passwordFilled && !passwordValid && (
                    <p
                      style={{
                        fontSize: "12px",
                        color: "#ef4444",
                        margin: "2px 0 0",
                      }}
                    >
                      Password does not meet all requirements.
                    </p>
                  )}
                  {submitted && !id && !passwordFilled && (
                    <p
                      style={{
                        fontSize: "12px",
                        color: "#ef4444",
                        margin: "2px 0 0",
                      }}
                    >
                      Password is required.
                    </p>
                  )}
                </div>

                <div className="col-span-12 sm:col-span-6">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Confirm Password{!id && <RequiredStar />}
                  </label>
                  <div className="relative">
                    <input
                      type={eyes.confirmPassword ? "text" : "password"}
                      className={`relative  bg-[#F7F7F7] w-full rounded-lg h-10 focus:outline focus:outline-[1px] focus:outline-[#e5e7eb] focus:ring-0 px-3 disabled:cursor-not-allowed pr-10 ${id && !passwordFilled ? "opacity-40 cursor-not-allowed" : ""}`}
                      placeholder="Confirm password"
                      value={form.confirmPassword}
                      maxLength={16}
                      disabled={id && !passwordFilled}
                      onChange={(e) =>
                        setForm({ ...form, confirmPassword: e.target.value })
                      }
                      required={!id || passwordFilled}
                    />
                    <div className="absolute top-1/2 -translate-y-1/2 right-3 cursor-pointer">
                      <i
                        onClick={() => {
                          if (!id || passwordFilled)
                            setEyes({
                              ...eyes,
                              confirmPassword: !eyes.confirmPassword,
                            });
                        }}
                      >
                        {eyes.confirmPassword ? (
                          <LuEye className="text-gray-400" />
                        ) : (
                          <LuEyeOff className="text-gray-400" />
                        )}
                      </i>
                    </div>
                  </div>
                  <div style={{ height: "20px", marginTop: "4px" }}>
                    {passwordFilled && form.confirmPassword.length > 0 ? (
                      <p
                        style={{
                          fontSize: "12px",
                          color: confirmMatch ? "#22c55e" : "#ef4444",
                          transition: "color 0.2s",
                          margin: 0,
                        }}
                      >
                        {confirmMatch
                          ? "✓ Passwords match"
                          : "✗ Passwords does not match"}
                      </p>
                    ) : submitted && passwordFilled && !form.confirmPassword ? (
                      <p
                        style={{
                          fontSize: "12px",
                          color: "#ef4444",
                          margin: 0,
                        }}
                      >
                        Please confirm your password.
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════
                LOCATIONS — multiple cards
            ══════════════════════════════════════════════════════════════ */}
            <div>
              {/* Section-level header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex gap-2 items-center">
                  <IoLocation className="text-[#CC9900] text-xl" />
                  <h4 className="text-[#2D2D2D] font-semibold text-base">
                    Locations
                  </h4>
                </div>
                <button
                  type="button"
                  onClick={addLocation}
                  className="flex items-center gap-1.5 text-sm font-medium text-white bg-[#CC9900] hover:opacity-80 px-4 py-2 rounded-full transition"
                >
                  <MdOutlineAddLocationAlt size={16} />
                  Add Location
                </button>
              </div>

              <div className="grid grid-cols-1 gap-5">
                {form.locations.map((loc, locIndex) => (
                  <div
                    key={locIndex}
                    className="bg-white rounded-[12px] shadow-[0px_3px_14px_-8px_#888888] border overflow-hidden"
                  >
                    {/* Location card header */}
                    <div className="flex items-center justify-between px-5 py-3 bg-[#faf7ed] border-b">
                      <div className="flex items-center gap-2">
                        <MdOutlinePlace className="text-[#CC9900] text-lg" />
                        <span className="font-semibold text-[#2D2D2D] text-sm">
                          Location {locIndex + 1}
                          {loc.city ? ` — ${loc.city}` : ""}
                        </span>
                      </div>
                      {form.locations.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            if (loc?.id) {
                              setDeletedIds((prev) => ({
                                ...prev,
                                locations: [...prev.locations, loc?.id],
                              }));
                            }
                            removeLocation(locIndex);
                          }}
                          className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 font-medium transition"
                        >
                          <IoCloseOutline size={16} />
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="p-5 grid grid-cols-1 gap-6">
                      {/* ── Address fields ── */}
                      <div>
                        <div className="grid grid-cols-12 gap-5">
                          <div className="col-span-12">
                            <GooglePlaceAutoComplete
                              apiKey={environment.map_key}
                              value={loc.address}
                              label="Address"
                              required={locIndex === 0}
                              onSelect={(result) => {
                                setForm((f) => {
                                  const locations = [...f.locations];
                                  locations[locIndex] = {
                                    ...locations[locIndex],
                                    address: result.address,
                                    city: result.city,
                                    state: result.state,
                                    country: result.country,
                                    pinCode: result.pinCode,
                                    latitude: result.latitude.toString(),
                                    longitude: result.longitude.toString(),
                                  };
                                  return { ...f, locations };
                                });
                              }}
                            />
                            <ErrorMsg
                              show={submitted && locIndex === 0 && !loc.address}
                              msg="Address is required"
                            />
                          </div>

                          <div className="col-span-12 sm:col-span-6">
                            <label className="block mb-2 text-sm font-medium text-gray-700">
                              City {locIndex === 0 && <RequiredStar />}
                            </label>
                            <input
                              type="text"
                              className="relative bg-[#F7F7F7] w-full rounded-lg h-10 focus:outline focus:outline-[1px] focus:outline-[#e5e7eb] focus:ring-0 px-3 disabled:cursor-not-allowed"
                              value={loc.city}
                              onChange={(e) =>
                                updateLocation(locIndex, "city", e.target.value)
                              }
                            />
                            <ErrorMsg
                              show={submitted && locIndex === 0 && !loc.city}
                              msg="City is required"
                            />
                          </div>

                          <div className="col-span-12 sm:col-span-6">
                            <label className="block mb-2 text-sm font-medium text-gray-700">
                              State
                            </label>
                            <input
                              type="text"
                              className="relative bg-[#F7F7F7] w-full rounded-lg h-10 focus:outline focus:outline-[1px] focus:outline-[#e5e7eb] focus:ring-0 px-3 disabled:cursor-not-allowed"
                              value={loc.state}
                              onChange={(e) =>
                                updateLocation(
                                  locIndex,
                                  "state",
                                  e.target.value,
                                )
                              }
                            />
                          </div>

                          <div className="col-span-12 sm:col-span-6">
                            <label className="block mb-2 text-sm font-medium text-gray-700">
                              Country {locIndex === 0 && <RequiredStar />}
                            </label>
                            <input
                              type="text"
                              className="relative bg-[#F7F7F7] w-full rounded-lg h-10 focus:outline focus:outline-[1px] focus:outline-[#e5e7eb] focus:ring-0 px-3 disabled:cursor-not-allowed"
                              value={loc.country}
                              onChange={(e) =>
                                updateLocation(
                                  locIndex,
                                  "country",
                                  e.target.value,
                                )
                              }
                            />
                            <ErrorMsg
                              show={submitted && locIndex === 0 && !loc.country}
                              msg="Country is required"
                            />
                          </div>

                          <div className="col-span-12 sm:col-span-6">
                            <label className="block mb-2 text-sm font-medium text-gray-700">
                              Pin Code
                            </label>
                            <input
                              type="text"
                              className="relative bg-[#F7F7F7] w-full rounded-lg h-10 focus:outline focus:outline-[1px] focus:outline-[#e5e7eb] focus:ring-0 px-3 disabled:cursor-not-allowed"
                              value={loc.pinCode}
                              onChange={(e) =>
                                updateLocation(
                                  locIndex,
                                  "pinCode",
                                  methodModel.isNumber(e),
                                )
                              }
                            />
                          </div>
                        </div>
                      </div>

                      {/* ── Divider ── */}
                      <hr className="border-gray-100" />

                      {/* ── Operating Hours ── */}
                      <div>
                        <div className="mb-4 flex items-start justify-between gap-3">
                          <div>
                            <div className="flex gap-2 items-center">
                              <IoIosHourglass className="text-[#CC9900] text-xl" />
                              <h4 className="text-[#2D2D2D] font-semibold text-sm">
                                Operating Hours
                              </h4>
                            </div>
                          </div>

                          {/* "Same as previous" checkbox — shown for index >= 1 */}
                          {locIndex > 0 && (
                            <label className="flex items-center gap-2 cursor-pointer select-none bg-[#fdf9ec] border border-[#f0e0a0] rounded-lg px-3 py-1.5 flex-shrink-0">
                              <input
                                type="checkbox"
                                className="w-4 h-4 accent-[#CC9900] cursor-pointer"
                                checked={loc.sameAsPrevious}
                                onChange={() => toggleSameAsPrevious(locIndex)}
                              />
                              <span className="text-xs font-medium text-[#8a6b00]">
                                Same as Location {locIndex}
                              </span>
                            </label>
                          )}
                        </div>

                        <div
                          className={`grid grid-cols-12 gap-5 ${loc.sameAsPrevious ? "opacity-50 pointer-events-none" : ""}`}
                        >
                          {DAYS.map((day) => {
                            const closed = isClosedDay(locIndex, day);
                            return (
                              <div
                                key={day}
                                className="col-span-12 lg:col-span-6"
                              >
                                <div className="flex items-center gap-3">
                                  <span className="min-w-[78px] w-[78px] text-sm font-medium text-gray-600 capitalize flex-shrink-0">
                                    {day}
                                  </span>
                                  <input
                                    type="text"
                                    className={`relative bg-[#F7F7F7] w-full rounded-lg h-10 focus:outline focus:outline-[1px] focus:outline-[#e5e7eb] focus:ring-0 px-3 disabled:cursor-not-allowed ${closed ? "opacity-30 pointer-events-none" : ""}`}
                                    value={closed ? "" : loc.hours[day] || ""}
                                    onChange={(e) =>
                                      setHour(locIndex, day, e.target.value)
                                    }
                                    disabled={closed}
                                  />
                                  <label className="flex items-center gap-1.5 cursor-pointer flex-shrink-0 select-none">
                                    <div
                                      className={`relative w-9 h-5 rounded-full transition-colors duration-200 ${closed ? "bg-red-400" : "bg-[#119a11]"}`}
                                      onClick={() => toggleDay(locIndex, day)}
                                    >
                                      <div
                                        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${closed ? "translate-x-4" : "translate-x-0"}`}
                                      />
                                    </div>
                                    <span
                                      className={`text-xs font-medium ${closed ? "text-red-400" : "text-gray-400"}`}
                                    >
                                      {closed ? "Closed" : "Open"}
                                    </span>
                                  </label>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-xs text-gray-400 mt-3">
                          Toggle a day to mark it as closed — no hours will be
                          sent for closed days.
                        </p>
                      </div>

                      {/* ── Divider ── */}
                      <hr className="border-gray-100" />

                      {/* ── Stations ── */}
                      <div>
                        <div className="mb-4 flex items-center justify-between">
                          <div className="flex gap-2 items-center">
                            <TbBuildingStore className="text-[#CC9900] text-xl" />
                            <div>
                              <h4 className="text-[#2D2D2D] font-semibold text-sm">
                                Stations
                              </h4>
                              <p className="text-xs text-[#717182]">
                                Add stations within this location
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => addStation(locIndex)}
                            className="flex items-center gap-1 text-xs font-medium text-[#CC9900] border border-[#CC9900] hover:bg-[#faf7ed] px-3 py-1.5 rounded-full transition"
                          >
                            <FiPlus size={13} />
                            Add Station
                          </button>
                        </div>

                        <div className="grid grid-cols-1 gap-3">
                          {loc.stations.map((station, stationIndex) => (
                            <div
                              key={stationIndex}
                              className="flex items-center gap-3"
                            >
                              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-[#faf7ed] border border-[#f0e0a0] text-[#CC9900] text-xs font-semibold flex-shrink-0">
                                {stationIndex + 1}
                              </div>
                              <input
                                type="text"
                                className="relative bg-[#F7F7F7] w-full rounded-lg h-10 focus:outline focus:outline-[1px] focus:outline-[#e5e7eb] focus:ring-0 px-3"
                                placeholder={`Station name (e.g. Station ${String.fromCharCode(65 + stationIndex)})`}
                                value={station.name}
                                onChange={(e) =>
                                  updateStation(
                                    locIndex,
                                    stationIndex,
                                    e.target.value,
                                  )
                                }
                              />
                              {loc.stations.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (station?.id) {
                                      setDeletedIds((prev) => ({
                                        ...prev,
                                        stations: [
                                          ...prev.stations,
                                          station?.id,
                                        ],
                                      }));
                                    }
                                    removeStation(locIndex, stationIndex);
                                  }}
                                  className="flex-shrink-0 text-gray-400 hover:text-red-500 transition"
                                >
                                  <IoCloseOutline size={20} />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                        {loc.stations.length === 0 && (
                          <p className="text-xs text-gray-400 mt-2">
                            No stations added yet. Click "Add Station" to add
                            one.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Bottom add-location button */}
              <button
                type="button"
                onClick={addLocation}
                className="mt-4 w-full flex items-center justify-center gap-2 text-sm font-medium text-[#CC9900] border border-dashed border-[#CC9900] hover:bg-[#faf7ed] py-3 rounded-[12px] transition"
              >
                <MdOutlineAddLocationAlt size={18} />
                Add Another Location
              </button>
            </div>

            {/* ── Amenities ── */}
            {/* <div className="bg-white p-5 rounded-[12px] shadow-[0px_3px_14px_-8px_#888888] border">
              <div className='mb-5'>
                <div className='flex gap-2'>
                  <CgFormatCenter className="text-[#CC9900] text-xl relative top-[1px]" />
                  <SectionHead title="Amenities" />
                </div>
                <p className='text-sm text-[#717182]'></p>
              </div>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="Select amenities"
                value={form.amenities}
                onChange={(v) => setForm({ ...form, amenities: v })}
                optionFilterProp="children"
              >
                {AMENITY_OPTIONS.map((a) => <Option key={a} value={a}>{a}</Option>)}
              </Select>
            </div> */}

            {/* ── Amenities ── */}
            <div className="bg-white p-5 rounded-[12px] shadow-[0px_3px_14px_-8px_#888888] border">
              <div className="mb-5">
                <div className="flex gap-2">
                  <CgFormatCenter className="text-[#CC9900] text-xl relative top-[1px]" />
                  <SectionHead title="Amenities" />
                </div>
                <p className="text-sm text-[#717182]">
                  Select available amenities for this venue
                </p>
              </div>

              <Select
                mode="multiple"
                className="w-full"
                placeholder="Select amenities"
                value={form.amenities}
                onChange={handleAmenityChange}
                optionFilterProp="children"
              >
                {AMENITY_OPTIONS.map((amenity) => (
                  <Option key={amenity} value={amenity}>
                    {amenity}
                  </Option>
                ))}
              </Select>

              {showCustomAmenity && (
                <div className="flex items-center gap-2 mt-3">
                  <input
                    type="text"
                    value={customAmenity}
                    onChange={(e) => setCustomAmenity(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        submitCustomAmenity();
                      }

                      if (e.key === "Escape") {
                        cancelCustomAmenity();
                      }
                    }}
                    placeholder="Enter your amenity"
                    autoFocus
                    className="bg-[#F7F7F7] w-full rounded-lg h-10 px-3 text-sm focus:outline focus:outline-[1px] focus:outline-[#CC9900]"
                  />

                  <button
                    type="button"
                    onClick={submitCustomAmenity}
                    className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-[#CC9900] text-white hover:opacity-80 transition"
                    title="Add amenity"
                  >
                    <IoCheckmark size={22} />
                  </button>

                  <button
                    type="button"
                    onClick={cancelCustomAmenity}
                    className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-gray-100 text-gray-500 hover:bg-red-50 hover:text-red-500 transition"
                    title="Cancel"
                  >
                    <IoCloseOutline size={22} />
                  </button>
                </div>
              )}
            </div>

            {/* ── Logo ── */}
            <div className="bg-white p-5 rounded-[12px] shadow-[0px_3px_14px_-8px_#888888] border">
              <div className="mb-5">
                <div className="flex gap-2">
                  <LuImage className="text-[#CC9900] text-xl relative top-[1px]" />
                  <SectionHead title="Logo" />
                </div>
                <p className="text-sm text-[#717182]"></p>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  {form.logo ? (
                    <div className="relative inline-block">
                      <img
                        src={methodModel.noImg(form.logo, "venue")}
                        className="w-[120px] h-[120px] object-contain rounded-lg border"
                        alt="Logo"
                      />
                      <button
                        type="button"
                        className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow-md hover:text-red-600"
                        onClick={() => setForm({ ...form, logo: "" })}
                      >
                        <IoCloseOutline size={18} />
                      </button>
                    </div>
                  ) : (
                    <label className="flex gap-2 items-center justify-center cursor-pointer bg-white font-medium rounded-lg text-sm px-5 py-3 border border-dashed border-gray-400 text-gray-600 hover:bg-gray-50">
                      <FiPlus /> Upload Logo
                      <input
                        type="file"
                        className="hidden"
                        accept="image/png,image/jpeg"
                        onChange={handleLogoUpload}
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>

            {/* ── Venue Images ── */}
            <div className="bg-white p-5 rounded-[12px] shadow-[0px_3px_14px_-8px_#888888] border">
              <div className="mb-5">
                <div className="flex gap-2">
                  <IoMdImages className="text-[#CC9900] text-xl relative top-[1px]" />
                  <SectionHead title="Venue Images" />
                </div>
                <p className="text-sm text-[#717182]"></p>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <div className="flex flex-wrap gap-3">
                    {form.images.map((img, i) => (
                      <div key={i} className="relative">
                        <img
                          src={methodModel.noImg(img, "venue")}
                          className="w-[120px] h-[90px] object-cover rounded-lg border"
                          alt={`venue-${i}`}
                        />
                        <button
                          type="button"
                          className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow-md hover:text-red-600"
                          onClick={() => removeImage(i)}
                        >
                          <IoCloseOutline size={18} />
                        </button>
                      </div>
                    ))}
                    <label className="w-full flex flex-col items-center justify-center h-[90px] border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 text-gray-400 text-sm text-center p-5">
                      <FiUpload className="mb-1" size={18} />
                      Add Image
                      <input
                        type="file"
                        multiple
                        className="hidden"
                        accept="image/png,image/jpeg"
                        onChange={handleImagesUpload}
                      />
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-2 justify-end mt-6">
            <button
              type="submit"
              className="bg-primary leading-10 h-10 flex justify-center items-center shadow-btn px-6 hover:opacity-80 text-sm text-white rounded-full gap-1 cursor-pointer min-w-[94px]"
            >
              {id ? "Update Venue" : "Create Venue"}
            </button>
          </div>
        </div>
      </form>
    </Layout>
  );
}

export default AddEditVenue;
